import type { Sound } from './supabase-types'
import { TESLA_FACTS } from './tesla-facts'
import { fmtDurationPrecise } from './tls-format'

/**
 * The duration ceiling comes from lib/tesla-facts.ts, per docs/ANTI_SLOP.md:
 * a spec never gets restated locally. This file used to hardcode 2 and tell
 * the user "Tesla allows up to 2s", while the trimmer, the voice generator
 * and lib/audio-utils all used 5. The site contradicted itself, and both
 * numbers were framed as a Tesla limit that tesla-facts.ts explicitly does
 * not claim: the duration guidance is RECOMMENDED, not a published spec.
 */
const TESLA_MAX_DURATION = TESLA_FACTS.audio.recommended.maxDurationSeconds
const QUIET_VOLUME_THRESHOLD = 60

export function getDurationHint(sound: Sound): string | null {
  if (!sound.duration || sound.duration <= TESLA_MAX_DURATION) return null
  // m:ss.SS, the precision form. "3.4s" was the banned shape and it sat one
  // line from the spec sheet's own 0:03.37.
  return `This sound is ${fmtDurationPrecise(sound.duration)}. Keep a lock chime to ${TESLA_FACTS.audio.recommended.duration}.`
}

export function getVolumeHint(sound: Sound): string | null {
  if (sound.volume_db === undefined || sound.volume_db === null) return null
  if (sound.volume_db >= QUIET_VOLUME_THRESHOLD) return null
  return 'This sound is on the quieter side. Crank Tesla volume to 7+ for best effect.'
}

export function getNormalizationBadge(sound: Sound): 'normalized' | 'quiet' | null {
  if (sound.is_normalized) return 'normalized'
  if (sound.volume_db !== undefined && sound.volume_db !== null && sound.volume_db < QUIET_VOLUME_THRESHOLD) return 'quiet'
  return null
}

/**
 * Returns the highest-priority pre-step install hint for the accordion, or null.
 * Priority: duration > volume > normalization.
 */
export function getInstallHint(sound: Sound): string | null {
  if (sound.duration && sound.duration > TESLA_MAX_DURATION) {
    return `This sound is longer than the recommended ${TESLA_FACTS.audio.recommended.duration}. Use the trim panel above before downloading.`
  }
  if (typeof sound.volume_db === 'number' && sound.volume_db < QUIET_VOLUME_THRESHOLD) {
    return 'This sound is on the quieter side. Crank Tesla volume to 7+ for best effect.'
  }
  if (sound.is_normalized === false) {
    return 'Volume not normalized. Preview before installing.'
  }
  return null
}
